import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectVersionsQuery,
  GetProjectVersionsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectVersionsState
  extends MergeRequestState<GetProjectVersionsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectVersions,
  isFetching: (state: ReduxState) => state.server.getProjectVersions.isFetching,
  data: (state: ReduxState) => state.server.getProjectVersions.data,
  error: (state: ReduxState) => state.server.getProjectVersions.error,
};

const initialState: GetProjectVersionsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectVersions';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetProjectVersionsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectVersions(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectVersions {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectVersions: ServerGetProjectVersions = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
